/**
 * Rush Order Management
 * Shared logic for handling rush order products in cart
 */

class RushOrderManager {
  constructor(config) {
    this.rush3VariantId = config.rush3VariantId;
    this.rushNextVariantId = config.rushNextVariantId;
    this.rushEligibleCount = config.rushEligibleCount;
    this.rushEligibleProductIds = config.rushEligibleProductIds || [];
    this.onUpdateCallback = config.onUpdate || null;

    this.rush3Checkbox = null;
    this.rushNextCheckbox = null;
    this.rushNaCheckbox = null;
    this.statusMessage = null;
    this.isSyncing = false;
  }

  /**
   * Initialize the rush order UI
   * @param {string} rush3CheckboxId - ID of the 3-day rush checkbox
   * @param {string} rushNextCheckboxId - ID of the next-day rush checkbox
   * @param {string} statusMessageId - ID of the status message element
   * @param {string} rushNaCheckboxId - ID of the N/A checkbox (optional)
   */
  init(rush3CheckboxId, rushNextCheckboxId, statusMessageId, rushNaCheckboxId = null) {
    this.rush3Checkbox = document.getElementById(rush3CheckboxId);
    this.rushNextCheckbox = document.getElementById(rushNextCheckboxId);
    this.rushNaCheckbox = rushNaCheckboxId ? document.getElementById(rushNaCheckboxId) : null;
    this.statusMessage = document.getElementById(statusMessageId);

    if (!this.rush3Checkbox || !this.rushNextCheckbox) {
      console.warn("Rush order checkboxes not found");
      return;
    }

    // Initialize modal
    this.initModal();

    // Attach event listeners IMMEDIATELY (only add once)
    if (!this.rush3Checkbox.hasAttribute("data-rush-initialized")) {
      this.rush3Checkbox.addEventListener("change", (e) => this.onCheckboxChange(e));
      this.rush3Checkbox.setAttribute("data-rush-initialized", "true");
    }
    if (!this.rushNextCheckbox.hasAttribute("data-rush-initialized")) {
      this.rushNextCheckbox.addEventListener("change", (e) => this.onCheckboxChange(e));
      this.rushNextCheckbox.setAttribute("data-rush-initialized", "true");
    }
    if (this.rushNaCheckbox && !this.rushNaCheckbox.hasAttribute("data-rush-initialized")) {
      this.rushNaCheckbox.addEventListener("change", (e) => this.onCheckboxChange(e));
      this.rushNaCheckbox.setAttribute("data-rush-initialized", "true");
    }

    // Sync in background (don't await, don't block)
    this.syncRushProductQuantity()
      .then(() => {
        return this.syncCheckboxes();
      })
      .catch((err) => {
        console.error("Rush order sync error:", err);
      });
  }

  /**
   * Initialize modal event listeners
   */
  initModal() {
    const modal = document.getElementById("rush-order-info-modal");
    if (!modal) return;

    const closeBtn = modal.querySelector(".rush-order-modal__close");
    const okBtn = modal.querySelector(".rush-order-modal__ok-btn");
    const overlay = modal.querySelector(".rush-order-modal__overlay");
    const dontShowCheckbox = document.getElementById("rush-order-modal-dont-show");

    // Close button - cancel the pending action
    if (closeBtn) {
      closeBtn.addEventListener("click", () => {
        this.hideRushModal(false);
        this.cancelPendingAction();
      });
    }

    // OK button - proceed with pending action
    if (okBtn) {
      okBtn.addEventListener("click", () => {
        const dontShow = dontShowCheckbox ? dontShowCheckbox.checked : false;
        this.hideRushModal(dontShow);
        this.processPendingAction();
      });
    }

    // Overlay click to close - cancel the pending action
    if (overlay) {
      overlay.addEventListener("click", () => {
        this.hideRushModal(false);
        this.cancelPendingAction();
      });
    }
  }

  /**
   * Show rush order modal if not previously seen
   * @returns {boolean} True if modal was shown, false if already seen
   */
  showRushModal() {
    // Check if user has already seen the modal
    if (localStorage.getItem("rush-order-modal-seen")) {
      return false;
    }

    const modal = document.getElementById("rush-order-info-modal");
    if (modal) {
      modal.setAttribute("data-show", "true");
      return true;
    }

    return false;
  }

  /**
   * Hide rush order modal
   * @param {boolean} dontShowAgain - If true, save preference to localStorage
   */
  hideRushModal(dontShowAgain) {
    const modal = document.getElementById("rush-order-info-modal");
    if (modal) {
      modal.setAttribute("data-show", "false");
    }

    if (dontShowAgain) {
      localStorage.setItem("rush-order-modal-seen", "true");
    }

    // Reset checkbox
    const dontShowCheckbox = document.getElementById("rush-order-modal-dont-show");
    if (dontShowCheckbox) {
      dontShowCheckbox.checked = false;
    }
  }

  /**
   * Process the pending checkbox action after modal OK is clicked
   */
  processPendingAction() {
    if (this.pendingCheckbox) {
      const checkbox = this.pendingCheckbox;
      this.pendingCheckbox = null;

      // Set flag to skip modal check during processing
      this.isProcessingPendingAction = true;

      // Trigger the checkbox change again, but skip the modal
      this.onCheckboxChange({ target: checkbox });
    }
  }

  /**
   * Cancel the pending action (uncheck the checkbox)
   */
  cancelPendingAction() {
    if (this.pendingCheckbox) {
      this.pendingCheckbox.checked = false;
      this.pendingCheckbox = null;
    }
  }

  /**
   * Fetch current cart JSON
   */
  async fetchCart() {
    const response = await fetch("/cart.js");
    return await response.json();
  }

  /**
   * Remove all rush products from cart
   */
  async removeRushProducts() {
    const cart = await this.fetchCart();
    const updates = {};

    cart.items.forEach((item) => {
      if (item.id === this.rush3VariantId || item.id === this.rushNextVariantId) {
        updates[item.key] = 0;
      }
    });

    if (Object.keys(updates).length === 0) return;

    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates }),
    });

    return response.json();
  }

  /**
   * Add rush product with quantity = eligible count
   */
  async addRushProduct(variantId) {
    const body = {
      items: [
        {
          id: variantId,
          quantity: this.rushEligibleCount,
        },
      ],
    };

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return response.json();
  }

  /**
   * Update quantity for specific rush product key
   */
  async updateRushProductQuantity(key, quantity) {
    const updates = {};
    updates[key] = quantity;

    await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates }),
    });
  }

  /**
   * Calculate rush eligible count from current cart
   * (only items with product_id in rushEligibleProductIds AND "custom" in variant)
   */
  calculateEligibleCount(cart) {
    let count = 0;
    cart.items.forEach((item) => {
      const variantTitle = (item.variant_title || '').toLowerCase();
      if (variantTitle.includes('custom')) {
        count += item.quantity;
      }
    });
    return count;
  }

  /**
   * Update the quantity of existing rush product in cart if it doesn't match rushEligibleCount
   */
  async syncRushProductQuantity() {
    // Prevent recursive syncing
    if (this.isSyncing) return;
    this.isSyncing = true;

    try {
      const cart = await this.fetchCart();

      // Recalculate eligible count from current cart state
      this.rushEligibleCount = this.calculateEligibleCount(cart);

      const rush3Item = cart.items.find((i) => i.id === this.rush3VariantId);
      const rushNextItem = cart.items.find((i) => i.id === this.rushNextVariantId);

      let updated = false;

      if (rush3Item && rush3Item.quantity !== this.rushEligibleCount) {
        await this.updateRushProductQuantity(rush3Item.key, this.rushEligibleCount);
        updated = true;
      }

      if (rushNextItem && rushNextItem.quantity !== this.rushEligibleCount) {
        await this.updateRushProductQuantity(rushNextItem.key, this.rushEligibleCount);
        updated = true;
      }

      // If we updated quantities, refresh the UI
      if (updated) {
        await this.refreshCartUI();
      }
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Refresh the cart UI after updating rush products
   */
  async refreshCartUI() {
    // Fetch fresh cart data
    const cart = await this.fetchCart();

    // Trigger cart refresh for Vue drawer (refresh without closing/reopening)
    if (window.eventBus) {
      window.eventBus.emit("toggle:right:drawer", {
        type: "cart",
        forceOpen: true,
        params: { cart: cart },
      });
    }

    // Also dispatch standard cart events that themes might listen to
    document.dispatchEvent(new CustomEvent("cart:updated", { detail: cart }));
    document.dispatchEvent(new CustomEvent("cart:refresh", { detail: cart }));
  }

  /**
   * Update UI checkboxes based on cart content
   */
  async syncCheckboxes() {
    const cart = await this.fetchCart();

    const hasRush3 = cart.items.some((i) => i.id === this.rush3VariantId);
    const hasRushNext = cart.items.some((i) => i.id === this.rushNextVariantId);

    this.rush3Checkbox.checked = hasRush3;
    this.rushNextCheckbox.checked = hasRushNext;

    // If neither rush option is selected, ensure N/A is checked
    if (this.rushNaCheckbox) {
      this.rushNaCheckbox.checked = !hasRush3 && !hasRushNext;
    }
  }

  /**
   * Handler for checkbox changes
   */
  async onCheckboxChange(e) {
    const clickedCheckbox = e.target;

    // Handle N/A checkbox special logic
    if (this.rushNaCheckbox && clickedCheckbox === this.rushNaCheckbox) {
      // If N/A is being unchecked, prevent it and keep it checked
      if (!clickedCheckbox.checked) {
        clickedCheckbox.checked = true;
        return;
      }

      // If N/A is being checked, uncheck the other rush options
      if (clickedCheckbox.checked) {
        this.rush3Checkbox.checked = false;
        this.rushNextCheckbox.checked = false;

        // Remove any rush products from cart and reload
        if (this.statusMessage) {
          this.statusMessage.textContent = "Removing rush order...";
        }

        await this.removeRushProducts();

        // Set flag to reopen drawer after reload (if this is a drawer)
        if (clickedCheckbox.id.includes("-drawer")) {
          sessionStorage.setItem("reopen-cart-drawer", "true");
        }

        location.reload();
        return;
      }
    }

    // Show modal on first click and wait for user to close it (unless we're processing a pending action)
    if (clickedCheckbox.checked && !this.isProcessingPendingAction) {
      const modalShown = this.showRushModal();
      if (modalShown) {
        // Modal is being shown for the first time - wait for user to click OK
        // Store the pending checkbox action
        this.pendingCheckbox = clickedCheckbox;
        return; // Don't process yet, wait for modal OK
      }
    }

    // Clear the processing flag
    this.isProcessingPendingAction = false;

    // Disable both checkboxes while processing
    this.rush3Checkbox.disabled = true;
    this.rushNextCheckbox.disabled = true;
    if (this.statusMessage) {
      this.statusMessage.textContent = "Updating...";
    }

    // Uncheck the other checkbox if this one is being checked
    if (clickedCheckbox.checked) {
      // Uncheck N/A if it exists
      if (this.rushNaCheckbox) {
        this.rushNaCheckbox.checked = false;
      }

      if (clickedCheckbox === this.rush3Checkbox) {
        this.rushNextCheckbox.checked = false;
      } else if (clickedCheckbox === this.rushNextCheckbox) {
        this.rush3Checkbox.checked = false;
      }
    }

    // Remove any existing rush products from the cart first
    await this.removeRushProducts();

    // If the clicked checkbox is checked, add corresponding rush product
    if (clickedCheckbox.checked) {
      try {
        // Recalculate eligible count from current cart before adding
        const cart = await this.fetchCart();
        this.rushEligibleCount = this.calculateEligibleCount(cart);

        await this.addRushProduct(parseInt(clickedCheckbox.dataset.variant));
        if (this.statusMessage) {
          this.statusMessage.textContent = "Rush order updated. Reloading...";
        }

        // Set flag to reopen drawer after reload (if this is a drawer)
        if (clickedCheckbox.id.includes("-drawer")) {
          sessionStorage.setItem("reopen-cart-drawer", "true");
        }

        // Call the update callback if provided (for drawer refresh)
        if (this.onUpdateCallback) {
          await this.onUpdateCallback();
        }

        // Reload the page to reflect updated cart UI
        location.reload();
        return;
      } catch (error) {
        console.error("Error adding rush product:", error);
        if (this.statusMessage) {
          this.statusMessage.textContent = "Error updating rush order.";
        }
      }
    } else {
      // Checkbox unchecked means no rush product
      if (this.statusMessage) {
        this.statusMessage.textContent = "Rush order removed.";
      }

      // If unchecking 3-day or next-day, check N/A to ensure one is always selected
      if (this.rushNaCheckbox && (clickedCheckbox === this.rush3Checkbox || clickedCheckbox === this.rushNextCheckbox)) {
        this.rushNaCheckbox.checked = true;
      }

      // Set flag to reopen drawer after reload (if this is a drawer)
      if (clickedCheckbox.id.includes("-drawer")) {
        sessionStorage.setItem("reopen-cart-drawer", "true");
      }

      // Call the update callback if provided (for drawer refresh)
      if (this.onUpdateCallback) {
        await this.onUpdateCallback();
      }

      // Reload to reflect removal
      location.reload();
      return;
    }

    // Re-enable checkboxes (won't get here if page reloads)
    this.rush3Checkbox.disabled = false;
    this.rushNextCheckbox.disabled = false;
  }

  /**
   * Check if a cart item is a rush variant
   */
  isRushVariant(variantId) {
    return variantId === this.rush3VariantId || variantId === this.rushNextVariantId;
  }

  /**
   * Update the eligible count (useful when cart changes)
   */
  updateEligibleCount(newCount) {
    this.rushEligibleCount = newCount;
  }

  /**
   * Start monitoring cart changes and auto-sync rush products
   */
  startCartMonitoring() {
    // Track last cart state to avoid unnecessary syncs
    this.lastCartHash = null;

    // Listen for cart update events (common in Shopify themes)
    this.cartUpdateHandler = () => {
      this.syncRushProductQuantity().catch((err) => {
        console.error("Error syncing rush products:", err);
      });
    };

    // Listen for various cart events
    document.addEventListener("cart:updated", this.cartUpdateHandler);
    document.addEventListener("cart.requestComplete", this.cartUpdateHandler);

    // Also poll periodically as fallback (check every 2 seconds, but only sync if changed)
    this.pollInterval = setInterval(async () => {
      try {
        const cart = await this.fetchCart();
        const cartHash = JSON.stringify(cart.items.map((i) => ({ id: i.id, qty: i.quantity })));

        // Only sync if cart has changed
        if (cartHash !== this.lastCartHash) {
          this.lastCartHash = cartHash;
          await this.syncRushProductQuantity();
        }
      } catch (err) {
        console.error("Error checking cart:", err);
      }
    }, 2000);
  }

  /**
   * Stop monitoring cart changes
   */
  stopCartMonitoring() {
    if (this.cartUpdateHandler) {
      document.removeEventListener("cart:updated", this.cartUpdateHandler);
      document.removeEventListener("cart.requestComplete", this.cartUpdateHandler);
      this.cartUpdateHandler = null;
    }

    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }

    this.lastCartHash = null;
  }
}

// Export for use in other scripts
if (typeof module !== "undefined" && module.exports) {
  module.exports = RushOrderManager;
}
